import { useId, type ReactNode } from 'react';
import { Form } from 'react-bootstrap';

import { InfoTooltip } from 'components/base/InfoTooltip';

interface EditBooleanProps {
  readonly label: string;
  readonly value: boolean;
  readonly onChange: (value: boolean) => void;
  /**
   * Explanatory text, shown in a tooltip on an info icon beside the label —
   * deliberately NOT inline under the control like `Field`'s `helpText`. A
   * checkbox's guidance is typically a sentence or two on what on-vs-off means,
   * and a column of checkboxes each carrying that inline is mostly noise; the
   * tooltip keeps it one hover away instead.
   */
  readonly info?: ReactNode;
}

/**
 * Checkbox wrapper. No `mandatory` — a checkbox is always either
 * checked or unchecked. `id` is auto-generated via `useId()` so the
 * label-for/input-id binding is unique without the caller having to
 * manage it.
 *
 * The info icon is a sibling of the `Form.Check`, not part of its `label`, so
 * the checkbox's accessible name stays exactly the label text.
 */
export const EditBoolean = ({ label, value, onChange, info }: EditBooleanProps) => {
  const id = useId();
  // align-items-baseline, not -center: the info badge is sized in `em` and
  // carries the label's baseline, so baseline alignment is what puts it on the
  // label's line rather than floating beside it.
  return (
    <div className="d-flex align-items-baseline gap-2">
      <Form.Check type="checkbox" id={id} label={label} checked={value} onChange={(e) => onChange(e.target.checked)} />
      {info !== undefined && <InfoTooltip describes={label}>{info}</InfoTooltip>}
    </div>
  );
};
